import { supabase } from '../supabaseClient';
import { resolveSiteTimeZone } from '../utils/siteScheduling';
import { payrollService } from './payrollService';
import type {
  PayPeriod,
  PayPeriodFrequency,
  PayPeriodStatus,
  PayrollAggregateRow,
  PayrollExportResult,
  Timesheet,
  TimesheetAuditEvent,
  TimesheetEntry,
  TimesheetEntryType,
  TimesheetStatus,
} from './payroll/types';

/**
 * Pay periods and the manager review workflow.
 *
 * Generating a period snapshots the calculated figures into timesheets and
 * timesheet_entries. Everything downstream (approval, export, aggregate
 * reporting) then reads the snapshot, so an approved run keeps reporting what
 * was approved even if the roster is edited afterwards.
 */

const QUERY_PAGE_SIZE = 1000;

const PAY_PERIOD_TRANSITIONS: Record<PayPeriodStatus, PayPeriodStatus | null> = {
  open: 'in_review',
  in_review: 'approved',
  approved: 'locked',
  locked: 'paid',
  paid: null,
};

interface CurrentProfile {
  id: string;
  company_id: string;
  role: string;
}

interface PayPeriodRow {
  id: string;
  company_id: string;
  label: string;
  frequency: string;
  start_date: string;
  end_date: string;
  status: string;
  pay_date: string | null;
  notes: string | null;
  calculation_context: unknown;
  submitted_at: string | null;
  approved_at: string | null;
  locked_at: string | null;
  paid_at: string | null;
  approver?: { full_name: string | null } | { full_name: string | null }[] | null;
}

interface TimesheetRow {
  id: string;
  company_id: string;
  pay_period_id: string;
  guard_id: string;
  status: string;
  pay_band_id: string | null;
  pay_band_code: string | null;
  base_hourly_rate: number | string | null;
  payroll_reference: string | null;
  cost_centre: string | null;
  ordinary_minutes: number;
  overtime_minutes: number;
  double_time_minutes: number;
  unpaid_break_minutes: number;
  sunday_minutes: number;
  public_holiday_minutes: number;
  unsocial_minutes: number;
  leave_minutes: number;
  minimum_top_up_minutes: number;
  shift_count: number;
  flagged_count: number;
  ordinary_pay: number | string;
  overtime_pay: number | string;
  premium_pay: number | string;
  adjustment_pay: number | string;
  expense_pay: number | string;
  deduction_pay: number | string;
  gross_pay: number | string;
  leave_accrued_hours: number | string;
  calculated_at: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  rejected_at: string | null;
  rejection_reason: string | null;
  manager_note: string | null;
  guard?: { full_name: string | null } | { full_name: string | null }[] | null;
  approver?: { full_name: string | null } | { full_name: string | null }[] | null;
}

interface TimesheetEntryRow {
  id: string;
  timesheet_id: string;
  shift_execution_id: string | null;
  site_id: string | null;
  entry_type: string;
  work_date: string;
  description: string | null;
  source_start_at: string | null;
  source_end_at: string | null;
  effective_start_at: string | null;
  effective_end_at: string | null;
  ordinary_minutes: number;
  overtime_minutes: number;
  double_time_minutes: number;
  unpaid_break_minutes: number;
  sunday_minutes: number;
  public_holiday_minutes: number;
  unsocial_minutes: number;
  minimum_top_up_minutes: number;
  rounding_adjustment_minutes: number;
  quantity: number | string | null;
  unit_rate: number | string | null;
  rate_snapshot: number | string | null;
  ordinary_pay: number | string;
  overtime_pay: number | string;
  premium_pay: number | string;
  total_pay: number | string;
  is_manual: boolean;
  is_edited: boolean;
  edit_reason: string | null;
  edited_at: string | null;
  flags: string[] | null;
  metadata?: Record<string, unknown> | null;
  site?: {
    name: string | null;
    timezone: string | null;
    country_code: string | null;
  } | {
    name: string | null;
    timezone: string | null;
    country_code: string | null;
  }[] | null;
  editor?: { full_name: string | null } | { full_name: string | null }[] | null;
}

interface TimesheetLifecycleContext {
  id: string;
  status: TimesheetStatus;
  payPeriodId: string;
  payPeriodStatus: PayPeriodStatus;
}

interface LockableTimesheetRow {
  id: string;
  status: TimesheetStatus;
}

const PAY_PERIOD_SELECT = `
  id, company_id, label, frequency, start_date, end_date, status, pay_date, notes,
  calculation_context, submitted_at, approved_at, locked_at, paid_at,
  approver:profiles!pay_periods_approved_by_fkey(full_name)
`;

const TIMESHEET_SELECT = `
  id, company_id, pay_period_id, guard_id, status, pay_band_id, pay_band_code,
  base_hourly_rate, payroll_reference, cost_centre,
  ordinary_minutes, overtime_minutes, double_time_minutes, unpaid_break_minutes,
  sunday_minutes, public_holiday_minutes, unsocial_minutes, leave_minutes,
  minimum_top_up_minutes, shift_count, flagged_count,
  ordinary_pay, overtime_pay, premium_pay, adjustment_pay, expense_pay,
  deduction_pay, gross_pay, leave_accrued_hours,
  calculated_at, submitted_at, approved_at, rejected_at, rejection_reason, manager_note,
  guard:profiles!timesheets_guard_id_fkey(full_name),
  approver:profiles!timesheets_approved_by_fkey(full_name)
`;

const TIMESHEET_ENTRY_SELECT = `
  id, timesheet_id, shift_execution_id, site_id, entry_type, work_date, description,
  source_start_at, source_end_at, effective_start_at, effective_end_at,
  ordinary_minutes, overtime_minutes, double_time_minutes, unpaid_break_minutes,
  sunday_minutes, public_holiday_minutes, unsocial_minutes, minimum_top_up_minutes,
  rounding_adjustment_minutes, quantity, unit_rate, rate_snapshot,
  ordinary_pay, overtime_pay, premium_pay, total_pay,
  is_manual, is_edited, edit_reason, edited_at, flags, metadata,
  site:sites!timesheet_entries_site_id_fkey(name, timezone, country_code),
  editor:profiles!timesheet_entries_edited_by_fkey(full_name)
`;

const num = (value: number | string | null | undefined): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const numOrNull = (value: number | string | null | undefined): number | null => {
  if (value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

function unwrap<T>(value: T | T[] | null | undefined): T | null {
  if (Array.isArray(value)) return value[0] ?? null;
  return value ?? null;
}

const minutes = (hours: number): number => Math.round(hours * 60);

interface QueryPageResult {
  data: unknown[] | null;
  error: unknown;
}

/** Supabase projects cap SELECT responses, so exhaust stable ordered pages. */
async function fetchAllPages<T>(
  fetchPage: (from: number, to: number) => PromiseLike<QueryPageResult>,
): Promise<T[]> {
  const rows: T[] = [];

  for (let from = 0; ; from += QUERY_PAGE_SIZE) {
    const { data, error } = await fetchPage(from, from + QUERY_PAGE_SIZE - 1);
    if (error) throw error;

    const page = (data ?? []) as T[];
    rows.push(...page);
    if (page.length < QUERY_PAGE_SIZE) return rows;
  }
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ');
}

async function getCurrentProfile(requireAdmin = false): Promise<CurrentProfile> {
  const { data: { user }, error: authError } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('profiles')
    .select('id, company_id, role')
    .eq('id', user.id)
    .single();

  if (error) throw error;
  if (!data?.company_id) throw new Error('This account is not attached to a company.');
  if (requireAdmin && data.role !== 'admin') {
    throw new Error('Only company admins can do that.');
  }
  if (data.role !== 'admin' && data.role !== 'manager') {
    throw new Error('Only company admins and managers can access timesheets.');
  }

  return data as CurrentProfile;
}

async function getTimesheetLifecycleContext(
  companyId: string,
  timesheetId: string,
): Promise<TimesheetLifecycleContext> {
  const { data: sheetData, error: sheetError } = await supabase
    .from('timesheets')
    .select('id, pay_period_id, status')
    .eq('id', timesheetId)
    .eq('company_id', companyId)
    .single();

  if (sheetError) throw sheetError;

  const sheet = sheetData as {
    id: string;
    pay_period_id: string;
    status: TimesheetStatus;
  };
  const { data: periodData, error: periodError } = await supabase
    .from('pay_periods')
    .select('status')
    .eq('id', sheet.pay_period_id)
    .eq('company_id', companyId)
    .single();

  if (periodError) throw periodError;

  return {
    id: sheet.id,
    status: sheet.status,
    payPeriodId: sheet.pay_period_id,
    payPeriodStatus: (periodData as { status: PayPeriodStatus }).status,
  };
}

function assertTimesheetCanBeEdited(context: TimesheetLifecycleContext): void {
  if (
    context.payPeriodStatus === 'approved'
    || context.payPeriodStatus === 'locked'
    || context.payPeriodStatus === 'paid'
  ) {
    throw new Error(`This pay period is ${context.payPeriodStatus} and can no longer be edited.`);
  }
  if (context.status === 'approved' || context.status === 'locked') {
    throw new Error(`This timesheet is ${context.status} and can no longer be edited.`);
  }
}

function mapPayPeriod(row: PayPeriodRow, timesheetCount = 0, approvedCount = 0): PayPeriod {
  const context = row.calculation_context;
  const settings = context && typeof context === 'object' && !Array.isArray(context)
    ? (context as { settings?: unknown }).settings
    : null;
  const rawCurrency = settings && typeof settings === 'object' && !Array.isArray(settings)
    ? (settings as { currencyCode?: unknown }).currencyCode
    : null;
  const currencyCode = typeof rawCurrency === 'string'
    ? rawCurrency.trim().toUpperCase()
    : '';

  return {
    id: row.id,
    companyId: row.company_id,
    label: row.label,
    frequency: row.frequency as PayPeriodFrequency | 'custom',
    startDate: row.start_date,
    endDate: row.end_date,
    status: row.status as PayPeriodStatus,
    payDate: row.pay_date,
    notes: row.notes,
    calculationCurrencyCode: /^[A-Z]{3}$/.test(currencyCode) ? currencyCode : null,
    timesheetCount,
    approvedCount,
    submittedAt: row.submitted_at,
    approvedAt: row.approved_at,
    approvedByName: unwrap(row.approver)?.full_name ?? null,
    lockedAt: row.locked_at,
    paidAt: row.paid_at,
  };
}

function mapTimesheet(row: TimesheetRow): Timesheet {
  return {
    id: row.id,
    companyId: row.company_id,
    payPeriodId: row.pay_period_id,
    guardId: row.guard_id,
    guardName: unwrap(row.guard)?.full_name?.trim() || 'Unknown guard',
    status: row.status as TimesheetStatus,
    payBandId: row.pay_band_id,
    payBandCode: row.pay_band_code,
    baseHourlyRate: numOrNull(row.base_hourly_rate),
    payrollReference: row.payroll_reference,
    costCentre: row.cost_centre,
    ordinaryMinutes: row.ordinary_minutes,
    overtimeMinutes: row.overtime_minutes,
    doubleTimeMinutes: row.double_time_minutes,
    unpaidBreakMinutes: row.unpaid_break_minutes,
    sundayMinutes: row.sunday_minutes,
    publicHolidayMinutes: row.public_holiday_minutes,
    unsocialMinutes: row.unsocial_minutes,
    leaveMinutes: row.leave_minutes,
    minimumTopUpMinutes: row.minimum_top_up_minutes,
    shiftCount: row.shift_count,
    flaggedCount: row.flagged_count,
    ordinaryPay: num(row.ordinary_pay),
    overtimePay: num(row.overtime_pay),
    premiumPay: num(row.premium_pay),
    adjustmentPay: num(row.adjustment_pay),
    expensePay: num(row.expense_pay),
    deductionPay: num(row.deduction_pay),
    grossPay: num(row.gross_pay),
    leaveAccruedHours: num(row.leave_accrued_hours),
    calculatedAt: row.calculated_at,
    submittedAt: row.submitted_at,
    approvedAt: row.approved_at,
    approvedByName: unwrap(row.approver)?.full_name ?? null,
    rejectedAt: row.rejected_at,
    rejectionReason: row.rejection_reason,
    managerNote: row.manager_note,
  };
}

function mapEntry(row: TimesheetEntryRow): TimesheetEntry {
  const site = unwrap(row.site);
  const snapshottedTimeZone = typeof row.metadata?.work_timezone === 'string'
    ? row.metadata.work_timezone.trim()
    : '';
  const workTimeZone = resolveSiteTimeZone({
    timezone: snapshottedTimeZone || site?.timezone || null,
    country_code: site?.country_code ?? 'IE',
  }) || 'Europe/Dublin';

  return {
    id: row.id,
    timesheetId: row.timesheet_id,
    shiftExecutionId: row.shift_execution_id,
    siteId: row.site_id,
    siteName: site?.name ?? null,
    workTimeZone,
    entryType: row.entry_type as TimesheetEntryType,
    workDate: row.work_date,
    description: row.description,
    sourceStartAt: row.source_start_at,
    sourceEndAt: row.source_end_at,
    effectiveStartAt: row.effective_start_at,
    effectiveEndAt: row.effective_end_at,
    ordinaryMinutes: row.ordinary_minutes,
    overtimeMinutes: row.overtime_minutes,
    doubleTimeMinutes: row.double_time_minutes,
    unpaidBreakMinutes: row.unpaid_break_minutes,
    sundayMinutes: row.sunday_minutes,
    publicHolidayMinutes: row.public_holiday_minutes,
    unsocialMinutes: row.unsocial_minutes,
    minimumTopUpMinutes: row.minimum_top_up_minutes,
    roundingAdjustmentMinutes: row.rounding_adjustment_minutes,
    quantity: numOrNull(row.quantity),
    unitRate: numOrNull(row.unit_rate),
    rateSnapshot: numOrNull(row.rate_snapshot),
    ordinaryPay: num(row.ordinary_pay),
    overtimePay: num(row.overtime_pay),
    premiumPay: num(row.premium_pay),
    totalPay: num(row.total_pay),
    isManual: row.is_manual,
    isEdited: row.is_edited,
    editReason: row.edit_reason,
    editedByName: unwrap(row.editor)?.full_name ?? null,
    editedAt: row.edited_at,
    flags: row.flags ?? [],
  };
}

export interface CreatePayPeriodInput {
  label: string;
  frequency: PayPeriodFrequency | 'custom';
  startDate: string;
  endDate: string;
  payDate?: string | null;
  notes?: string | null;
}

export interface ManualEntryInput {
  timesheetId: string;
  entryType: TimesheetEntryType;
  workDate: string;
  description: string;
  /** Hours for time-based types; units for expenses and bonuses. */
  quantity: number;
  unitRate: number;
  siteId?: string | null;
}

export const timesheetService = {
  async listPayPeriods(): Promise<PayPeriod[]> {
    const profile = await getCurrentProfile();

    const [data, counts] = await Promise.all([
      fetchAllPages<PayPeriodRow>((from, to) => supabase
        .from('pay_periods')
        .select(PAY_PERIOD_SELECT)
        .eq('company_id', profile.company_id)
        .order('start_date', { ascending: false })
        .order('id', { ascending: false })
        .range(from, to)),
      fetchAllPages<{
        pay_period_id: string;
        total_count: number | string;
        approved_count: number | string;
      }>((from, to) => supabase
        .rpc('get_pay_period_timesheet_counts')
        .order('pay_period_id', { ascending: true })
        .range(from, to)),
    ]);

    const totals = new Map(counts.map((row) => [row.pay_period_id, {
      total: num(row.total_count),
      approved: num(row.approved_count),
    }] as const));

    return data.map((row) => {
      const count = totals.get(row.id) ?? { total: 0, approved: 0 };
      return mapPayPeriod(row, count.total, count.approved);
    });
  },

  async createPayPeriod(input: CreatePayPeriodInput): Promise<PayPeriod> {
    const profile = await getCurrentProfile();

    const { data, error } = await supabase
      .from('pay_periods')
      .insert({
        company_id: profile.company_id,
        label: input.label.trim(),
        frequency: input.frequency,
        start_date: input.startDate,
        end_date: input.endDate,
        pay_date: input.payDate ?? null,
        notes: input.notes ?? null,
        created_by: profile.id,
      })
      .select(PAY_PERIOD_SELECT)
      .single();

    if (error) {
      if ((error as { code?: string }).code === '23505') {
        throw new Error('Those dates overlap an existing pay period.');
      }
      throw error;
    }

    return mapPayPeriod(data as PayPeriodRow);
  },

  async updatePayPeriodPayDate(payPeriodId: string, payDate: string): Promise<void> {
    const profile = await getCurrentProfile();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(payDate)) throw new Error('Choose a valid employee pay date.');

    const { error } = await supabase
      .from('pay_periods')
      .update({ pay_date: payDate })
      .eq('id', payPeriodId)
      .eq('company_id', profile.company_id)
      .in('status', ['open', 'in_review'])
      .select('id')
      .single();

    if (error) throw new Error('The pay date can only be changed while the period is open or in review.');
  },

  /**
   * Calculate the period and write the result to timesheets/timesheet_entries.
   * Re-running replaces the generated lines but preserves manual ones, so a
   * manager's adjustments survive a recalculation after a roster fix.
   */
  async generateTimesheets(payPeriodId: string): Promise<{ timesheetCount: number; entryCount: number }> {
    const profile = await getCurrentProfile();

    const { data: periodRow, error: periodError } = await supabase
      .from('pay_periods')
      .select('id, company_id, start_date, end_date, status')
      .eq('id', payPeriodId)
      .eq('company_id', profile.company_id)
      .single();

    if (periodError) throw periodError;
    const period = periodRow as { id: string; start_date: string; end_date: string; status: string };

    if (period.status !== 'open' && period.status !== 'in_review') {
      throw new Error(
        `Timesheets can only be recalculated while a pay period is open or in review. This period is ${statusLabel(period.status)}.`,
      );
    }

    const result: PayrollExportResult = await payrollService.fetchPayrollExport({
      period: 'week',
      startDate: period.start_date,
      endDate: period.end_date,
    });

    const rowsByEmployee = new Map<string, PayrollExportResult['rows']>();
    result.rows.forEach((row) => {
      const employeeRows = rowsByEmployee.get(row.employeeId) ?? [];
      employeeRows.push(row);
      rowsByEmployee.set(row.employeeId, employeeRows);
    });

    const calculatedTimesheets = result.summaries.map((summary) => ({
      guard_id: summary.employeeId,
      pay_band_id: summary.payBandId,
      pay_band_code: summary.payBandCode,
      base_hourly_rate: summary.hourlyRate,
      payroll_reference: summary.payrollReference,
      cost_centre: summary.costCentre,
      leave_accrued_hours: summary.leaveAccruedHours,
      entries: (rowsByEmployee.get(summary.employeeId) ?? []).map((row) => {
        const overtimePay = row.overtimePay + row.doubleTimePay;
        const premiumPay = row.sundayPremiumPay + row.publicHolidayPremiumPay + row.unsocialPremiumPay;
        const totalPay = Math.round(
          (row.ordinaryPay + overtimePay + premiumPay + Number.EPSILON) * 100,
        ) / 100;

        return {
          shift_execution_id: row.shiftExecutionId,
          site_id: row.siteId,
          work_date: row.rosterDate,
          work_timezone: row.timeZone,
          description: row.siteName,
          source_start_at: row.sourceStartAt,
          source_end_at: row.sourceEndAt,
          effective_start_at: row.effectiveStartAt,
          effective_end_at: row.effectiveEndAt,
          ordinary_minutes: minutes(row.ordinaryHours),
          overtime_minutes: minutes(row.overtimeHours),
          double_time_minutes: minutes(row.doubleTimeHours),
          unpaid_break_minutes: minutes(row.unpaidBreakHours),
          sunday_minutes: minutes(row.sundayHours),
          public_holiday_minutes: minutes(row.publicHolidayHours),
          unsocial_minutes: minutes(row.unsocialHours),
          minimum_top_up_minutes: minutes(row.minimumShiftTopUpHours),
          rounding_adjustment_minutes: minutes(row.roundingAdjustmentHours),
          rate_snapshot: row.hourlyRate,
          ordinary_pay: row.ordinaryPay,
          overtime_pay: overtimePay,
          premium_pay: premiumPay,
          total_pay: totalPay,
          flags: row.flags,
        };
      }),
    }));

    const { data: applied, error: applyError } = await supabase.rpc('apply_pay_period_calculation', {
      p_pay_period_id: payPeriodId,
      p_calculated_at: result.generatedAt,
      p_calculation_context: {
        generatedAt: result.generatedAt,
        settings: result.settings,
        warnings: result.warnings,
        unbandedEmployeeCount: result.unbandedEmployeeCount,
      },
      p_timesheets: calculatedTimesheets,
    });
    if (applyError) throw applyError;

    const appliedResult = applied as {
      timesheet_count?: number | string;
      generated_entry_count?: number | string;
    } | null;
    return {
      timesheetCount: num(appliedResult?.timesheet_count),
      entryCount: num(appliedResult?.generated_entry_count),
    };
  },

  async listTimesheets(payPeriodId: string): Promise<Timesheet[]> {
    const profile = await getCurrentProfile();

    const data = await fetchAllPages<TimesheetRow>((from, to) => supabase
      .from('timesheets')
      .select(TIMESHEET_SELECT)
      .eq('pay_period_id', payPeriodId)
      .eq('company_id', profile.company_id)
      .order('id', { ascending: true })
      .range(from, to));

    return data
      .map(mapTimesheet)
      .sort((left, right) => left.guardName.localeCompare(right.guardName));
  },

  async listEntries(timesheetId: string): Promise<TimesheetEntry[]> {
    const profile = await getCurrentProfile();

    const data = await fetchAllPages<TimesheetEntryRow>((from, to) => supabase
      .from('timesheet_entries')
      .select(TIMESHEET_ENTRY_SELECT)
      .eq('timesheet_id', timesheetId)
      .eq('company_id', profile.company_id)
      .order('work_date', { ascending: true })
      .order('id', { ascending: true })
      .range(from, to));

    return data.map(mapEntry);
  },

  async listAuditEvents(timesheetId: string): Promise<TimesheetAuditEvent[]> {
    const profile = await getCurrentProfile();

    const data = await fetchAllPages<{
      id: string;
      timesheet_id: string;
      timesheet_entry_id: string | null;
      event_type: string;
      from_status: string | null;
      to_status: string | null;
      summary: string | null;
      occurred_at: string;
      actor?: { full_name: string | null } | { full_name: string | null }[] | null;
    }>((from, to) => supabase
      .from('timesheet_audit_events')
      .select('id, timesheet_id, timesheet_entry_id, event_type, from_status, to_status, summary, occurred_at, actor:profiles!timesheet_audit_events_actor_id_fkey(full_name)')
      .eq('timesheet_id', timesheetId)
      .eq('company_id', profile.company_id)
      .order('occurred_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to));

    return data.map((row) => ({
      id: row.id,
      timesheetId: row.timesheet_id,
      timesheetEntryId: row.timesheet_entry_id,
      actorName: unwrap(row.actor)?.full_name ?? null,
      eventType: row.event_type,
      fromStatus: row.from_status,
      toStatus: row.to_status,
      summary: row.summary,
      occurredAt: row.occurred_at,
    }));
  },

  /** Non-shift pay: training, standby, call-out, bonus, expense, deduction. */
  async addManualEntry(input: ManualEntryInput): Promise<TimesheetEntry> {
    const profile = await getCurrentProfile();
    const context = await getTimesheetLifecycleContext(profile.company_id, input.timesheetId);
    assertTimesheetCanBeEdited(context);

    const { data, error } = await supabase
      .rpc('add_manual_timesheet_entry', {
        p_timesheet_id: input.timesheetId,
        p_entry_type: input.entryType,
        p_work_date: input.workDate,
        p_description: input.description.trim(),
        p_quantity: input.quantity,
        p_unit_rate: input.unitRate,
        p_site_id: input.siteId ?? null,
      })
      .single();

    if (error) throw error;

    return mapEntry(data as TimesheetEntryRow);
  },

  async deleteEntry(entryId: string, timesheetId: string): Promise<void> {
    const profile = await getCurrentProfile();
    const context = await getTimesheetLifecycleContext(profile.company_id, timesheetId);
    assertTimesheetCanBeEdited(context);

    const { error } = await supabase.rpc('delete_manual_timesheet_entry', {
      p_timesheet_id: timesheetId,
      p_entry_id: entryId,
    });
    if (error) throw error;
  },

  async setTimesheetStatus(
    timesheetId: string,
    status: TimesheetStatus,
    options: { reason?: string; note?: string } = {},
  ): Promise<void> {
    const profile = await getCurrentProfile();
    const rejectionReason = options.reason?.trim() ?? '';
    if (status === 'rejected' && !rejectionReason) {
      throw new Error('A rejection reason is required so the correction is clear in the audit trail.');
    }
    if (status === 'locked') {
      throw new Error('Timesheets are locked automatically when their pay period is locked.');
    }

    const context = await getTimesheetLifecycleContext(profile.company_id, timesheetId);
    if (
      context.payPeriodStatus === 'approved'
      || context.payPeriodStatus === 'locked'
      || context.payPeriodStatus === 'paid'
    ) {
      throw new Error(`This pay period is ${context.payPeriodStatus} and its timesheets cannot be changed.`);
    }
    if (context.status === 'locked') {
      throw new Error('This timesheet is locked and cannot be changed.');
    }
    if ((status === 'approved' || status === 'rejected') && context.payPeriodStatus !== 'in_review') {
      throw new Error('Timesheets can only be approved or rejected while the pay period is in review.');
    }

    const payload: Record<string, unknown> = { status };
    if (status === 'rejected') {
      payload.rejection_reason = rejectionReason;
    }
    if (options.note !== undefined) payload.manager_note = options.note;

    const { error } = await supabase
      .from('timesheets')
      .update(payload)
      .eq('id', timesheetId)
      .eq('company_id', profile.company_id)
      .eq('status', context.status)
      .select('id')
      .single();
    if (error) {
      throw new Error('The timesheet changed before its status could be updated. Refresh and try again.');
    }

  },

  async setPayPeriodStatus(payPeriodId: string, status: PayPeriodStatus): Promise<void> {
    // Locking and marking paid are the irreversible steps, so they are admin-only.
    const needsAdmin = status === 'locked' || status === 'paid';
    const profile = await getCurrentProfile(needsAdmin);

    const { data: periodData, error: periodReadError } = await supabase
      .from('pay_periods')
      .select('status')
      .eq('id', payPeriodId)
      .eq('company_id', profile.company_id)
      .single();
    if (periodReadError) throw periodReadError;

    const currentStatus = (periodData as { status: PayPeriodStatus }).status;
    const expectedStatus = PAY_PERIOD_TRANSITIONS[currentStatus];
    if (expectedStatus !== status) {
      if (!expectedStatus) {
        throw new Error('This pay period is paid and its status can no longer be changed.');
      }
      throw new Error(
        `Pay periods must move from ${statusLabel(currentStatus)} to ${statusLabel(expectedStatus)} next; they cannot move directly to ${statusLabel(status)}.`,
      );
    }

    const sheets = await fetchAllPages<LockableTimesheetRow>((from, to) => supabase
      .from('timesheets')
      .select('id, status')
      .eq('pay_period_id', payPeriodId)
      .eq('company_id', profile.company_id)
      .order('id', { ascending: true })
      .range(from, to));
    if (status === 'in_review' && sheets.length === 0) {
      throw new Error('Generate at least one timesheet before submitting this pay period for review.');
    }
    if (status === 'approved' || status === 'locked') {
      if (sheets.length === 0) {
        throw new Error('Generate at least one timesheet before approving or locking this pay period.');
      }
      const unapprovedCount = sheets.filter((sheet) => (
        status === 'locked'
          ? sheet.status !== 'approved' && sheet.status !== 'locked'
          : sheet.status !== 'approved'
      )).length;
      if (unapprovedCount > 0) {
        throw new Error(
          `Every timesheet must be approved before the pay period can be ${status}. ${unapprovedCount} timesheet(s) still need approval.`,
        );
      }
    }
    if (status === 'paid') {
      if (sheets.length === 0 || sheets.some((sheet) => sheet.status !== 'locked')) {
        throw new Error('Every timesheet must be locked before this pay period can be marked paid.');
      }
    }

    // The database performs approved-period locking atomically: it validates
    // and freezes every approved child, snapshots totals, and updates this
    // parent in the same transaction.
    const { error } = await supabase
      .from('pay_periods')
      .update({ status })
      .eq('id', payPeriodId)
      .eq('company_id', profile.company_id)
      .eq('status', currentStatus)
      .select('id')
      .single();

    if (error) {
      throw new Error('The pay period changed before its status could be updated. Refresh and try again.');
    }
  },

  async recordPeriodExport(payPeriodId: string, label: string): Promise<void> {
    await getCurrentProfile();
    const exportLabel = label.trim();
    if (!exportLabel) throw new Error('Give this payroll export a label for the audit trail.');

    const { error: auditError } = await supabase.rpc('record_pay_period_export', {
      p_pay_period_id: payPeriodId,
      p_label: exportLabel,
    });
    if (auditError) {
      throw new Error(`The export audit event could not be recorded: ${auditError.message}`);
    }
  },

  async fetchAggregateReport(
    payPeriodId: string,
    groupBy: 'employee' | 'site' | 'pay_band' | 'cost_centre' | 'entry_type',
  ): Promise<PayrollAggregateRow[]> {
    const profile = await getCurrentProfile();

    const data = await fetchAllPages<Record<string, string | number>>((from, to) => supabase
      .rpc('get_payroll_aggregate_report', {
        p_company_id: profile.company_id,
        p_pay_period_id: payPeriodId,
        p_group_by: groupBy,
      })
      .order('group_key', { ascending: true })
      .range(from, to));

    return data.map((row) => ({
      groupKey: String(row.group_key),
      groupLabel: String(row.group_label),
      employeeCount: num(row.employee_count),
      shiftCount: num(row.shift_count),
      payableHours: (num(row.ordinary_minutes) + num(row.overtime_minutes) + num(row.double_time_minutes)) / 60,
      ordinaryHours: num(row.ordinary_minutes) / 60,
      overtimeHours: num(row.overtime_minutes) / 60,
      doubleTimeHours: num(row.double_time_minutes) / 60,
      unsocialHours: num(row.unsocial_minutes) / 60,
      sundayHours: num(row.sunday_minutes) / 60,
      publicHolidayHours: num(row.public_holiday_minutes) / 60,
      ordinaryPay: num(row.ordinary_pay),
      overtimePay: num(row.overtime_pay),
      premiumPay: num(row.premium_pay),
      totalPay: num(row.total_pay),
    }));
  },
};
